import axios from 'axios';
import symfonyClient from '@/services/SymfonyClient';

export type UploadCompleteResponse = {
  id: string;
};

type UploadInitResponse = {
  id: string;
};

export type FileUploadError = {
  status: number | undefined;
  data: unknown;
};

export type UploadCompanyResourceFileParams = {
  companyId: string;
  file: File;
  metadata?: Record<string, string>;
};

const CHUNK_SIZE = 10 * 1024 * 1024;

export const uploadCompanyResourceFile = async ({
  companyId,
  file,
  metadata,
}: UploadCompanyResourceFileParams): Promise<
  { data: UploadCompleteResponse } | { error: FileUploadError }
> => {
  if (file.size === 0) {
    return {
      error: {
        status: 400,
        data: 'File is empty.',
      },
    };
  }

  try {
    const initUploadResponse = await symfonyClient.post<UploadInitResponse>(
      `/api/companies/${companyId}/file-uploads/init`,
      {
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type,
        objectType: 'resource',
        metadata,
      },
    );

    const { id: uploadId } = initUploadResponse.data;

    for (let partStart = 0; partStart < file.size; partStart += CHUNK_SIZE) {
      const partForm = new FormData();
      partForm.append('multipartFileUploadId', uploadId);
      partForm.append('partNumber', (partStart / CHUNK_SIZE + 1).toString());
      partForm.append(
        'fileChunk',
        file.slice(partStart, Math.min(partStart + CHUNK_SIZE, file.size)),
      );

      await symfonyClient.post(
        `/api/companies/${companyId}/file-uploads/upload-part`,
        partForm,
      );
    }

    const completeUploadResponse =
      await symfonyClient.post<UploadCompleteResponse>(
        `/api/companies/${companyId}/file-uploads/complete`,
        {
          multipartFileUploadId: uploadId,
        },
      );

    return { data: completeUploadResponse.data };
  } catch (error: unknown) {
    if (axios.isAxiosError(error)) {
      return {
        error: {
          status: error.response?.status,
          data: error.response?.data || error.message,
        },
      };
    }

    return {
      error: {
        status: 500,
        data: error instanceof Error ? error.message : 'Unknown error',
      },
    };
  }
};
